/**
 * A `ResponsiveTableColumn.header` for one sortable column. `ResponsiveTable` renders the SAME node as the desktop `<th>` and the card label,
 * so the interactive half is `hidden sm:*` and the label-only half is what the card shows.
 * Takes plain strings rather than `useTranslation`: this is a function called inside a render, not a component, so it may not call a hook.
 */
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import type { ReactNode } from 'react'

import type { SortState } from './tableSort'

export function sortableHeader<K extends string>({
  sortKey,
  label,
  ariaLabel,
  sort,
  onSort,
}: {
  sortKey: K
  label: string
  ariaLabel: string
  sort: SortState<K>
  onSort: (key: K) => void
}): ReactNode {
  const active = sort !== null && sort.key === sortKey
  const Glyph = !active ? ChevronsUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown
  return (
    <span className="block">
      <span className="block sm:hidden">{label}</span>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={ariaLabel}
        className={`hidden items-center gap-1 hover:text-slate-700 sm:inline-flex dark:hover:text-slate-200 ${
          active ? 'text-slate-700 dark:text-slate-200' : ''
        }`}
      >
        {label}
        <Glyph className={`h-3 w-3 ${active ? '' : 'opacity-40'}`} aria-hidden="true" />
      </button>
    </span>
  )
}
